import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from '@refinedev/core';
import { useList } from '@refinedev/core';
import { gql } from '@urql/core';
import { theme } from 'antd';
import {
  Card,
  Button,
  Tag,
  Empty,
  Spin,
  Tooltip,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  SearchOutlined,
  FileTextOutlined,
  InboxOutlined,
  CreditCardOutlined,
  PlusOutlined,
  ArrowRightOutlined,
  EyeOutlined,
  ContainerOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

// GraphQL Queries for each stage
const PROCUREMENT_PLANS_RECENT_QUERY = gql`
  query RecentProcurementPlans {
    procurementPlans(query: { paging: { limit: 5 }, sorting: [{ field: "updatedAt", direction: DESC }] }) {
      nodes {
        id
        planNumber
        title
        status
        totalAmount
        fiscalYear
        updatedAt
      }
    }
  }
`;

const PURCHASE_REQUESTS_RECENT_QUERY = gql`
  query RecentPurchaseRequests {
    purchaseRequests(query: { paging: { limit: 5 }, sorting: [{ field: "updatedAt", direction: DESC }] }) {
      nodes {
        id
        requestNo
        status
        totalAmount
        dateRequested
        updatedAt
      }
    }
  }
`;

const SOURCINGS_RECENT_QUERY = gql`
  query RecentSourcings {
    sourcings(query: { paging: { limit: 5 }, sorting: [{ field: "updatedAt", direction: DESC }] }) {
      nodes {
        id
        sourcingNo
        type
        status
        totalAmount
        currency
        updatedAt
      }
    }
  }
`;

const PURCHASE_ORDERS_RECENT_QUERY = gql`
  query RecentPurchaseOrders {
    purchaseOrders(query: { paging: { limit: 5 }, sorting: [{ field: "updatedAt", direction: DESC }] }) {
      nodes {
        id
        orderNo
        status
        totalAmount
        currency
        vendor {
          companyName
        }
        updatedAt
      }
    }
  }
`;

const RECEIVINGS_RECENT_QUERY = gql`
  query RecentReceivings {
    receivings(query: { paging: { limit: 5 }, sorting: [{ field: "updatedAt", direction: DESC }] }) {
      nodes {
        id
        receivingNo
        status
        totalReceivedValue
        poNo
        updatedAt
      }
    }
  }
`;

const INVOICES_RECENT_QUERY = gql`
  query RecentInvoices {
    invoices(query: { paging: { limit: 5 }, sorting: [{ field: "updatedAt", direction: DESC }] }) {
      nodes {
        id
        invoiceNumber
        status
        totalAmount
        currency
        supplierName
        updatedAt
      }
    }
  }
`;

const PAYMENT_REQUESTS_RECENT_QUERY = gql`
  query RecentPaymentRequests {
    paymentRequests(query: { paging: { limit: 5 }, sorting: [{ field: "updatedAt", direction: DESC }] }) {
      nodes {
        id
        status
        amount
        currency
        supplierName
        dueDate
        updatedAt
      }
    }
  }
`;

// Status color mappings
const statusColors: Record<string, string> = {
  DRAFT: 'default',
  SUBMITTED: 'processing',
  UNDER_REVIEW: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  PARTIALLY_APPROVED: 'purple',
  EXPIRED: 'red',
  VALIDATED: 'cyan',
  SENT_TO_VENDOR: 'blue',
  ACKNOWLEDGED: 'geekblue',
  IN_PROGRESS: 'gold',
  PARTIALLY_DELIVERED: 'lime',
  DELIVERED: 'green',
  COMPLETED: 'geekblue',
  CANCELLED: 'red',
  PUBLISHED: 'purple',
  CLOSED: 'orange',
  PENDING: 'processing',
  MATCHED: 'blue',
  PAID: 'success',
  RECEIVED: 'processing',
  DRAFT_ACCEPTANCE: 'blue',
  SUBMITTED_ACCEPTANCE: 'cyan',
  ACCEPTED: 'success',
  PARTIAL_ACCEPTED: 'warning',
};

// Stage definitions
interface Stage {
  key: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  createPath: string;
  listPath: string;
  query: any;
  resource: string;
  getItemTitle: (item: any) => string;
  getItemSubtitle: (item: any) => string;
  getItemStatus: (item: any) => string;
  getItemAmount: (item: any) => string;
}

const stages: Stage[] = [
  {
    key: 'procurementPlans',
    title: 'Procurement Plan',
    icon: <CalendarOutlined />,
    color: '#1890ff',
    bgColor: '#e6f7ff',
    createPath: '/procurement-plans/create',
    listPath: '/procurement-plans',
    query: PROCUREMENT_PLANS_RECENT_QUERY,
    resource: 'procurementPlans',
    getItemTitle: (item) => item.planNumber || item.title,
    getItemSubtitle: (item) => `FY ${item.fiscalYear}`,
    getItemStatus: (item) => item.status,
    getItemAmount: (item) => item.totalAmount ? `ETB ${item.totalAmount.toLocaleString()}` : '',
  },
  {
    key: 'purchaseRequests',
    title: 'Purchase Request',
    icon: <ShoppingCartOutlined />,
    color: '#52c41a',
    bgColor: '#f6ffed',
    createPath: '/purchase-requests/create',
    listPath: '/purchase-requests',
    query: PURCHASE_REQUESTS_RECENT_QUERY,
    resource: 'purchaseRequests',
    getItemTitle: (item) => item.requestNo,
    getItemSubtitle: (item) => item.dateRequested ? dayjs(item.dateRequested).format('MMM DD, YYYY') : '',
    getItemStatus: (item) => item.status,
    getItemAmount: (item) => item.totalAmount ? `ETB ${item.totalAmount.toLocaleString()}` : '',
  },
  {
    key: 'sourcings',
    title: 'Sourcing',
    icon: <SearchOutlined />,
    color: '#722ed1',
    bgColor: '#f9f0ff',
    createPath: '/sourcings/create',
    listPath: '/sourcings',
    query: SOURCINGS_RECENT_QUERY,
    resource: 'sourcings',
    getItemTitle: (item) => item.sourcingNo,
    getItemSubtitle: (item) => item.type || '',
    getItemStatus: (item) => item.status,
    getItemAmount: (item) => item.totalAmount ? `${item.currency || 'ETB'} ${item.totalAmount.toLocaleString()}` : '',
  },
  {
    key: 'purchaseOrders',
    title: 'Purchase Order',
    icon: <FileTextOutlined />,
    color: '#fa8c16',
    bgColor: '#fff7e6',
    createPath: '/purchase-orders/create',
    listPath: '/purchase-orders',
    query: PURCHASE_ORDERS_RECENT_QUERY,
    resource: 'purchaseOrders',
    getItemTitle: (item) => item.orderNo,
    getItemSubtitle: (item) => item.vendor?.companyName || '',
    getItemStatus: (item) => item.status,
    getItemAmount: (item) => item.totalAmount ? `${item.currency || 'ETB'} ${item.totalAmount.toLocaleString()}` : '',
  },
  {
    key: 'receivings',
    title: 'Receivings',
    icon: <InboxOutlined />,
    color: '#13c2c2',
    bgColor: '#e6fffb',
    createPath: '/receivings/create',
    listPath: '/receivings',
    query: RECEIVINGS_RECENT_QUERY,
    resource: 'receivings',
    getItemTitle: (item) => item.receivingNo,
    getItemSubtitle: (item) => item.poNo ? `PO: ${item.poNo}` : '',
    getItemStatus: (item) => item.status,
    getItemAmount: (item) => item.totalReceivedValue ? `ETB ${item.totalReceivedValue.toLocaleString()}` : '',
  },
  {
    key: 'invoices',
    title: 'Invoice',
    icon: <ContainerOutlined />,
    color: '#eb2f96',
    bgColor: '#fff0f6',
    createPath: '/invoices/create',
    listPath: '/invoices',
    query: INVOICES_RECENT_QUERY,
    resource: 'invoices',
    getItemTitle: (item) => item.invoiceNumber,
    getItemSubtitle: (item) => item.supplierName || '',
    getItemStatus: (item) => item.status,
    getItemAmount: (item) => item.totalAmount ? `${item.currency || 'ETB'} ${item.totalAmount.toLocaleString()}` : '',
  },
  {
    key: 'paymentRequests',
    title: 'Payment Request',
    icon: <CreditCardOutlined />,
    color: '#52c41a',
    bgColor: '#f6ffed',
    createPath: '/payment-requests/create',
    listPath: '/payment-requests',
    query: PAYMENT_REQUESTS_RECENT_QUERY,
    resource: 'paymentRequests',
    getItemTitle: (item) => item.id?.slice(0, 8) || 'Payment',
    getItemSubtitle: (item) => item.supplierName || (item.dueDate ? `Due: ${dayjs(item.dueDate).format('MMM DD')}` : ''),
    getItemStatus: (item) => item.status,
    getItemAmount: (item) => item.amount ? `${item.currency || 'ETB'} ${item.amount.toLocaleString()}` : '',
  },
];

// CSS Variables for dark/light mode
const getCssVariables = (isDark: boolean) => ({
  '--dashboard-bg': isDark ? '#141414' : '#fff',
  '--dashboard-bg-secondary': isDark ? '#1f1f1f' : '#fafafa',
  '--dashboard-bg-tertiary': isDark ? '#262626' : '#fff',
  '--dashboard-border': isDark ? '#303030' : '#f0f0f0',
  '--dashboard-text-primary': isDark ? '#fff' : 'inherit',
  '--dashboard-text-secondary': isDark ? '#8c8c8c' : 'inherit',
  '--dashboard-hover': isDark ? '#303030' : '#f5f5f5',
  '--dashboard-shadow': isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
});

// Flow Connector Component
const FlowConnector: React.FC = () => {
  const { token } = theme.useToken();
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 8px',
      color: token.colorTextQuaternary,
    }}>
      <ArrowRightOutlined style={{ fontSize: 20 }} />
    </div>
  );
};

// Stage Card Component
interface StageCardProps {
  stage: Stage;
  isLast: boolean;
  t: (key: string, defaultValue?: string) => string;
  isDark: boolean;
}

const StageCard: React.FC<StageCardProps> = ({ stage, isLast, t, isDark }) => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { data, isLoading } = useList({
    resource: stage.resource,
    meta: { gqlQuery: stage.query },
    pagination: { pageSize: 5 },
  });

  const items = (data?.data as any[]) || [];

  const handleItemClick = (itemId: string) => {
    navigate(`${stage.listPath}/show/${itemId}`);
  };

  const handleViewAll = () => {
    navigate(stage.listPath);
  };

  const cssVars = getCssVariables(isDark);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      <Card
        style={{
          width: 280,
          borderRadius: 12,
          border: `2px solid ${stage.color}`,
          boxShadow: cssVars['--dashboard-shadow'],
          overflow: 'hidden',
          background: cssVars['--dashboard-bg'],
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Header */}
        <div
          style={{
            background: stage.bgColor,
            padding: '16px',
            borderBottom: `1px solid ${stage.color}20`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: stage.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 18,
                }}
              >
                {stage.icon}
              </div>
              <div>
                <Text strong style={{ fontSize: 14, color: stage.color, display: 'block' }}>
                  {stage.title}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {items.length > 0 ? `${items.length} recent` : t('dashboard.noItems', 'No items')}
                </Text>
              </div>
            </div>
          </div>
          
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            block
            onClick={() => navigate(stage.createPath)}
            style={{
              background: stage.color,
              borderColor: stage.color,
            }}
          >
            {t('dashboard.createNew', 'Create New')}
          </Button>
        </div>

        {/* Recent Items List */}
        <div style={{ padding: '12px 16px', minHeight: 200 }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="small" />
            </div>
          ) : items.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('dashboard.noItems', 'No recent items')}
              style={{ margin: '20px 0' }}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((item, index) => (
                <Tooltip
                  key={item.id}
                  title={`Click to view details`}
                >
                  <div
                    onClick={() => handleItemClick(item.id)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: index % 2 === 0 ? cssVars['--dashboard-bg-secondary'] : cssVars['--dashboard-bg-tertiary'],
                      cursor: 'pointer',
                      border: `1px solid ${cssVars['--dashboard-border']}`,
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = stage.color;
                      e.currentTarget.style.boxShadow = `0 2px 8px ${stage.color}30`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = cssVars['--dashboard-border'];
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 13, color: cssVars['--dashboard-text-primary'], flex: 1 }} ellipsis>
                        {stage.getItemTitle(item)}
                      </Text>
                      <Tag
                        color={statusColors[stage.getItemStatus(item)] || 'default'}
                        style={{ fontSize: 10, margin: 0, padding: '0 4px', marginLeft: 8 }}
                      >
                        {stage.getItemStatus(item)}
                      </Tag>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                        {stage.getItemSubtitle(item)}
                      </Text>
                      {stage.getItemAmount(item) && (
                        <Text strong style={{ fontSize: 11, color: stage.color, marginLeft: 8 }}>
                          {stage.getItemAmount(item)}
                        </Text>
                      )}
                    </div>
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 16px',
            borderTop: `1px solid ${cssVars['--dashboard-border']}`,
            textAlign: 'center',
          }}
        >
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={handleViewAll}
            style={{ color: stage.color }}
          >
            {t('dashboard.viewAll', 'View All')}
          </Button>
        </div>
      </Card>

      {!isLast && <FlowConnector />}
    </div>
  );
};

// Stats Overview Component
const StatsOverview: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const { token } = theme.useToken();
  const cssVars = getCssVariables(isDark);
  
  const stats = [
    { label: 'Active Plans', value: 0, color: '#1890ff', icon: <CalendarOutlined /> },
    { label: 'Pending Requests', value: 0, color: '#52c41a', icon: <ShoppingCartOutlined /> },
    { label: 'Active Sourcing', value: 0, color: '#722ed1', icon: <SearchOutlined /> },
    { label: 'Open Orders', value: 0, color: '#fa8c16', icon: <FileTextOutlined /> },
    { label: 'Pending Invoices', value: 0, color: '#eb2f96', icon: <ContainerOutlined /> },
    { label: 'Payment Requests', value: 0, color: '#52c41a', icon: <CreditCardOutlined /> },
  ];

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      {stats.map((stat, index) => (
        <Col xs={12} sm={8} md={6} lg={4} key={index}>
          <Card
            style={{
              borderRadius: 8,
              borderLeft: `4px solid ${stat.color}`,
              background: cssVars['--dashboard-bg'],
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: `${stat.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: stat.color,
                  fontSize: 20,
                }}
              >
                {stat.icon}
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {stat.label}
                </Text>
                <Text strong style={{ fontSize: 24, color: stat.color }}>
                  {stat.value}
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

// Main Dashboard Component
export const Dashboard: React.FC = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const isDark = token.colorBgLayout === '#000000' || token.colorBgContainer === '#1f1f1f';

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 8 }}>
          {t('dashboard.title', 'Procurement Dashboard')}
        </Title>
        <Text type="secondary">
          {t('dashboard.summary', 'Track your procurement workflow from planning to payment')}
        </Text>
      </div>

      {/* Stats Overview */}
      <StatsOverview isDark={isDark} />

      {/* Flow Title */}
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('dashboard.procurementFlow', 'Procurement Flow')}
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('dashboard.navigateFlow', 'Navigate through your procurement pipeline')}
        </Text>
      </div>

      {/* Flow Container */}
      <div
        style={{
          display: 'flex',
          overflowX: 'auto',
          paddingBottom: 16,
          paddingTop: 8,
        }}
      >
        {stages.map((stage, index) => (
          <StageCard
            key={stage.key}
            stage={stage}
            isLast={index === stages.length - 1}
            t={t}
            isDark={isDark}
          />
        ))}
      </div>

      {/* Quick Actions */}
      <Card
        title={t('dashboard.quickActions', 'Quick Actions')}
        style={{ marginTop: 24, borderRadius: 12, background: isDark ? '#1f1f1f' : '#fff' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button
              type="primary"
              icon={<CalendarOutlined />}
              size="large"
              block
              onClick={() => window.location.href = '/procurement-plans/create'}
            >
              {t('procurementPlans.create', 'Create Plan')}
            </Button>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button
              icon={<ShoppingCartOutlined />}
              size="large"
              block
              onClick={() => window.location.href = '/purchase-requests/create'}
            >
              {t('purchaseRequests.create', 'New Purchase Request')}
            </Button>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button
              icon={<SearchOutlined />}
              size="large"
              block
              onClick={() => window.location.href = '/sourcings/create'}
            >
              {t('sourcings.create', 'New Sourcing')}
            </Button>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button
              icon={<CreditCardOutlined />}
              size="large"
              block
              onClick={() => window.location.href = '/payment-requests/create'}
            >
              {t('paymentRequests.create', 'New Payment Request')}
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );
};
